import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import parse from 'html-react-parser';
import { IconButton, Box, Typography } from '@mui/material';
import { css } from '@emotion/css';
import { PlayArrow, Pause, VolumeUp, VolumeOff, CloseFullscreen, OpenInFull } from '@mui/icons-material';
import { PlayerPlay, PlayerStop } from 'icons';
import throttle from 'lodash.throttle';
import { addSeconds, format } from 'date-fns';
import { useStyles } from './styles';

const Player = (props: any) => {
  const { url, height, width, className, controls, other, openFullscreen, isFullscreen, closeFullscreen, onPage, onReady, onError, disableFullscreen } = props;

  const [playing, setPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [played, setPlayed] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const [isReady, setIsReady] = useState<boolean>(false);

  const { classes, cx } = useStyles();

  const ref = React.useRef<ReactPlayer>(null);

  const PlayPauseBtn = () => {
    const ariaLabel = playing ? 'Pause' : 'Play';

    return (
      <IconButton aria-label={ariaLabel} onClick={handlePlayPause}>
        {playing ? <Pause /> : <PlayArrow />}
      </IconButton>
    );
  };

  const PlayStopBtn = () => {
    const ariaLabel = playing ? 'Stop' : 'Play';

    return (
      <IconButton
        aria-label={ariaLabel}
        onClick={(event: any) => {
          event.stopPropagation();
          event.preventDefault();
          handlePlayStop();
        }}
        className={classes.playStopBtn}
        onMouseDown={(event: any) => event.stopPropagation()}
      >
        {playing ? <PlayerStop /> : <PlayerPlay />}
      </IconButton>
    );
  };

  const MuteUnmuteBtn = () => {
    const ariaLabel = isMuted ? 'Unmute' : 'Mute';

    return (
      <IconButton aria-label={ariaLabel} onClick={handleMuteUnmute}>
        {isMuted ? <VolumeOff /> : <VolumeUp />}
      </IconButton>
    );
  };

  const OpenCloseBtn = () => {
    const ariaLabel = isFullscreen ? 'Close' : 'Open';

    return (
      <IconButton aria-label={ariaLabel} onClick={handleOpenClose}>
        {isFullscreen ? <CloseFullscreen /> : <OpenInFull />}
      </IconButton>
    );
  };

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleMuteUnmute = () => {
    setIsMuted(!isMuted);
  };

  const handlePlayStop = () => {
    if (playing) {
      ref.current?.seekTo(0);
    }
    setPlaying(!playing);
  };

  const handleOpenClose = () => {
    isFullscreen ? closeFullscreen() : openFullscreen();
  };

  const onProgress = throttle((prgrs: any) => {
    setPlayed(prgrs.playedSeconds);
    setProgress(+((played * 100) / duration).toFixed(0));
  }, 100);

  const onDuration = (seconds: number) => {
    setDuration(seconds);
  };

  const formatDuration = (seconds: number) => {
    const helperDate = addSeconds(new Date(0), seconds);
    return format(helperDate, 'mm:ss');
  };

  const handleOnReady = () => {
    setIsReady(true);
    onReady && onReady();
  };

  useEffect(() => {
    if (typeof isFullscreen === 'undefined') {
      return;
    }
    onPage && isFullscreen ? setPlaying(false) : setPlaying(true);
  }, [isFullscreen]);

  return (
    <Box
      className={cx(classes.wrapper, {
        isFullscreen,
        onPage,
      })}
    >
      <ReactPlayer
        {...other}
        ref={ref}
        url={url}
        className={cx(className, 'ReactPlayer')}
        playing={isReady && playing}
        height={height}
        width={width}
        muted={isMuted}
        loop={true}
        onProgress={controls ? onProgress : () => {}}
        onDuration={controls ? onDuration : () => {}}
        onReady={handleOnReady}
        onError={onError}
        config={{ file: { attributes: { playsInline: true } } }}
      />
      {controls && (
        <Box display="flex" alignItems="center" justifyContent="flex-start" className={cx(classes.controls, 'controls')}>
          <PlayPauseBtn />
          <Box display="flex" alignItems="center" className={classes.durationWrapper}>
            <Typography variant="subtitle1" className={classes.controlDuration}>
              {parse(`<span>${formatDuration(played)}</span> / <span>${formatDuration(duration)}</span>`)}
            </Typography>
          </Box>
          <progress max="100" value={progress} className={cx(classes.controlSeek, { preview: !isFullscreen && onPage })} />
          <div className={css({ flexGrow: isFullscreen && !onPage ? 0 : 1 })} />
          <MuteUnmuteBtn />
          {disableFullscreen || <OpenCloseBtn />}
        </Box>
      )}
      {controls || <PlayStopBtn />}
    </Box>
  );
};

Player.defaultProps = {
  isIcon: false,
  height: 540,
  width: 610,
  className: '',
};

export default Player;
